import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../commonDesign/Button'
import DateTimePicker from '../customDatePicker/StyledPicker'

dayjs.extend(utc)

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    padding: '1rem 0'
  },
  field: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem'
  },
  label: {
    fontSize: 'var(--font-size-small)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    minWidth: 75
  },
  picker: {
    '& input': {
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  error: {
    color: 'var(--color-danger-500, #D44333)',
    fontSize: 'var(--font-size-xsmall, 0.75rem)',
    marginTop: '0.25rem'
  }
}))

const today = () => dayjs().format('YYYY-MM-DD')
function CustomDateModal ({ open, onClose, onSubmit, dateFormat, initialStartDate, initialEndDate }) {
  const classes = useStyles()
  const [startDate, setStartDate] = useState(initialStartDate || today())
  const [endDate, setEndDate] = useState(initialEndDate || today())
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (open) {
      setStartDate(initialStartDate || today())
      setEndDate(initialEndDate || today())
      setErrors({})
    }
  }, [open, initialStartDate, initialEndDate])

  const handleStartDateChange = useCallback((date) => {
    setStartDate(dayjs.utc(date).format('YYYY-MM-DD'))
    setErrors(prev => ({ ...prev, startDate: null }))
  }, [])

  const handleEndDateChange = useCallback((date) => {
    setEndDate(dayjs.utc(date).format('YYYY-MM-DD'))
    setErrors(prev => ({ ...prev, endDate: null }))
  }, [])

  const validate = useCallback(() => {
    const newErrors = {}
    if (!startDate) newErrors.startDate = 'Start date is required'
    if (!endDate) newErrors.endDate = 'End date is required'
    if (startDate && endDate && dayjs.utc(endDate).isBefore(dayjs.utc(startDate))) {
      newErrors.endDate = 'End date must be after start date'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [startDate, endDate])

  const handleSubmit = useCallback(() => {
    if (validate()) {
      onSubmit(startDate, endDate)
    }
  }, [validate, startDate, endDate, onSubmit])

  const handleCancel = useCallback(() => {
    setErrors({})
    onClose()
  }, [onClose])

  return (
    <SydModal
      open={open}
      onClose={handleCancel}
      title='Custom Date Range'
      size='xsmall'
    >
      <div className={classes.container}>
        <div className={classes.field}>
          <span className={classes.label}>Start Date</span>
          <DateTimePicker
            type='date'
            format={dateFormat}
            disabledDateRange={{ max: today() }}
            onChange={handleStartDateChange}
            value={startDate}
            className={classes.picker}
          />
          {errors.startDate && <span className={classes.error}>{errors.startDate}</span>}
        </div>
        <div className={classes.field}>
          <span className={classes.label}>End Date</span>
          <DateTimePicker
            type='date'
            format={dateFormat}
            disabledDateRange={{ max: today() }}
            onChange={handleEndDateChange}
            value={endDate}
            className={classes.picker}
          />
          {errors.endDate && <span className={classes.error}>{errors.endDate}</span>}
        </div>
      </div>
      <SydModalActions>
        <SydButton variant='secondary' onClick={handleCancel}>
          Cancel
        </SydButton>
        <SydButton variant='primary' onClick={handleSubmit}>
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

CustomDateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dateFormat: PropTypes.string,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string
}

CustomDateModal.defaultProps = {
  dateFormat: 'MM/DD/YYYY',
  initialStartDate: null,
  initialEndDate: null
}

export default CustomDateModal
